import {
  EraNamespaceType,
  TBC_ANNIVERSARY_REALMS,
  getNamespaceTypeFromFlags,
  getNamespaceTypeForRealm,
  getProfileNamespace,
  getDynamicNamespace,
  getStaticNamespace,
  getRenderUrlPrefix
} from './namespaces';
import { RealmTypeFlags } from '../sharedTypes';

describe('WoW Era Namespaces', () => {
  describe('getNamespaceTypeFromFlags', () => {
    it('returns TBC_ANNIVERSARY for anniversary realm flags', () => {
      expect(getNamespaceTypeFromFlags(RealmTypeFlags.ANNIVERSARY_REALM)).toBe(EraNamespaceType.TBC_ANNIVERSARY);
      // Also works with combined flags
      expect(getNamespaceTypeFromFlags(RealmTypeFlags.ANNIVERSARY_REALM | RealmTypeFlags.PVP)).toBe(
        EraNamespaceType.TBC_ANNIVERSARY
      );
    });

    it('returns CLASSIC_ERA for non-anniversary realm flags', () => {
      expect(getNamespaceTypeFromFlags(RealmTypeFlags.NORMAL)).toBe(EraNamespaceType.CLASSIC_ERA);
      expect(getNamespaceTypeFromFlags(RealmTypeFlags.PVP)).toBe(EraNamespaceType.CLASSIC_ERA);
      expect(getNamespaceTypeFromFlags(RealmTypeFlags.HARDCORE)).toBe(EraNamespaceType.CLASSIC_ERA);
      expect(getNamespaceTypeFromFlags(0)).toBe(EraNamespaceType.CLASSIC_ERA);
    });
  });

  describe('getNamespaceTypeForRealm', () => {
    it('returns TBC_ANNIVERSARY for known anniversary realms', () => {
      expect(getNamespaceTypeForRealm('us', 'doomhowl')).toBe(EraNamespaceType.TBC_ANNIVERSARY);
      expect(getNamespaceTypeForRealm('eu', 'spineshatter')).toBe(EraNamespaceType.TBC_ANNIVERSARY);
      expect(getNamespaceTypeForRealm('kr', 'fengus-ferocity')).toBe(EraNamespaceType.TBC_ANNIVERSARY);
      expect(getNamespaceTypeForRealm('tw', 'moldars-moxie')).toBe(EraNamespaceType.TBC_ANNIVERSARY);
    });

    it('returns CLASSIC_ERA for non-anniversary realms', () => {
      expect(getNamespaceTypeForRealm('us', 'whitemane')).toBe(EraNamespaceType.CLASSIC_ERA);
      expect(getNamespaceTypeForRealm('eu', 'firemaw')).toBe(EraNamespaceType.CLASSIC_ERA);
      expect(getNamespaceTypeForRealm('kr', 'some-realm')).toBe(EraNamespaceType.CLASSIC_ERA);
    });

    it('is case sensitive for realm slugs', () => {
      expect(getNamespaceTypeForRealm('us', 'Doomhowl')).toBe(EraNamespaceType.CLASSIC_ERA);
      expect(getNamespaceTypeForRealm('us', 'DOOMHOWL')).toBe(EraNamespaceType.CLASSIC_ERA);
    });
  });

  describe('getProfileNamespace', () => {
    it('returns correct profile namespace for Classic Era', () => {
      expect(getProfileNamespace('us', EraNamespaceType.CLASSIC_ERA)).toBe('profile-classic1x-us');
      expect(getProfileNamespace('eu', EraNamespaceType.CLASSIC_ERA)).toBe('profile-classic1x-eu');
      expect(getProfileNamespace('kr', EraNamespaceType.CLASSIC_ERA)).toBe('profile-classic1x-kr');
      expect(getProfileNamespace('tw', EraNamespaceType.CLASSIC_ERA)).toBe('profile-classic1x-tw');
    });

    it('returns correct profile namespace for TBC Anniversary', () => {
      expect(getProfileNamespace('us', EraNamespaceType.TBC_ANNIVERSARY)).toBe('profile-classicann-us');
      expect(getProfileNamespace('eu', EraNamespaceType.TBC_ANNIVERSARY)).toBe('profile-classicann-eu');
      expect(getProfileNamespace('kr', EraNamespaceType.TBC_ANNIVERSARY)).toBe('profile-classicann-kr');
      expect(getProfileNamespace('tw', EraNamespaceType.TBC_ANNIVERSARY)).toBe('profile-classicann-tw');
    });
  });

  describe('getDynamicNamespace', () => {
    it('returns correct dynamic namespace for Classic Era', () => {
      expect(getDynamicNamespace('us', EraNamespaceType.CLASSIC_ERA)).toBe('dynamic-classic1x-us');
      expect(getDynamicNamespace('eu', EraNamespaceType.CLASSIC_ERA)).toBe('dynamic-classic1x-eu');
    });

    it('returns correct dynamic namespace for TBC Anniversary', () => {
      expect(getDynamicNamespace('us', EraNamespaceType.TBC_ANNIVERSARY)).toBe('dynamic-classicann-us');
      expect(getDynamicNamespace('eu', EraNamespaceType.TBC_ANNIVERSARY)).toBe('dynamic-classicann-eu');
    });
  });

  describe('getStaticNamespace', () => {
    it('returns correct static namespace for Classic Era', () => {
      expect(getStaticNamespace('us', EraNamespaceType.CLASSIC_ERA)).toBe('static-classic1x-us');
      expect(getStaticNamespace('eu', EraNamespaceType.CLASSIC_ERA)).toBe('static-classic1x-eu');
    });

    it('returns correct static namespace for TBC Anniversary', () => {
      expect(getStaticNamespace('us', EraNamespaceType.TBC_ANNIVERSARY)).toBe('static-classicann-us');
      expect(getStaticNamespace('eu', EraNamespaceType.TBC_ANNIVERSARY)).toBe('static-classicann-eu');
    });
  });

  describe('getRenderUrlPrefix', () => {
    it('returns correct render URL prefix for Classic Era', () => {
      expect(getRenderUrlPrefix('us', EraNamespaceType.CLASSIC_ERA)).toBe(
        '//render.worldofwarcraft.com/classic1x-us/character/'
      );
      expect(getRenderUrlPrefix('eu', EraNamespaceType.CLASSIC_ERA)).toBe(
        '//render.worldofwarcraft.com/classic1x-eu/character/'
      );
    });

    it('returns correct render URL prefix for TBC Anniversary', () => {
      expect(getRenderUrlPrefix('us', EraNamespaceType.TBC_ANNIVERSARY)).toBe(
        '//render.worldofwarcraft.com/classicann-us/character/'
      );
      expect(getRenderUrlPrefix('eu', EraNamespaceType.TBC_ANNIVERSARY)).toBe(
        '//render.worldofwarcraft.com/classicann-eu/character/'
      );
    });
  });

  describe('TBC_ANNIVERSARY_REALMS', () => {
    it('contains expected realms for US region', () => {
      const usRealms = TBC_ANNIVERSARY_REALMS.filter((r) => r.region === 'us');
      expect(usRealms.map((r) => r.slug)).toEqual(['doomhowl', 'dreamscythe', 'maladath', 'nightslayer']);
    });

    it('contains expected realms for EU region', () => {
      const euRealms = TBC_ANNIVERSARY_REALMS.filter((r) => r.region === 'eu');
      expect(euRealms.map((r) => r.slug)).toEqual(['soulseeker', 'spineshatter', 'thunderstrike']);
    });

    it('contains expected realms for KR region', () => {
      const krRealms = TBC_ANNIVERSARY_REALMS.filter((r) => r.region === 'kr');
      expect(krRealms.map((r) => r.slug)).toEqual(['fengus-ferocity', 'moldars-moxie', 'slipkiks-savvy']);
    });

    it('contains expected realms for TW region', () => {
      const twRealms = TBC_ANNIVERSARY_REALMS.filter((r) => r.region === 'tw');
      expect(twRealms.map((r) => r.slug)).toEqual(['fengus-ferocity', 'moldars-moxie', 'slipkiks-savvy']);
    });

    it('has correct total count', () => {
      expect(TBC_ANNIVERSARY_REALMS).toHaveLength(13);
    });
  });
});
